"use client";

import { useEffect, useRef, useState } from "react";

import type { ArchiveHookEvent, ThreadId } from "@taskin-code/contracts";

import { toastManager } from "~/components/ui/toast";
import { useArchiveHookStore, type ArchiveHookRun } from "~/archiveHookStore";
import { ArchiveHookOutputDialog } from "./ArchiveHookOutputDialog";

type ToastId = ReturnType<typeof toastManager.add>;
type ArchiveToastType = "info" | "loading" | "success" | "error" | "warning";
type Phase = ArchiveHookEvent["phase"];

/**
 * Bridges the ArchiveHook zustand store to the global toast manager and
 * hosts the read-only output modal. Mount once at the app root.
 */
export function ArchiveHookToastBridge() {
  const runs = useArchiveHookStore((state) => state.runs);
  const toastIdsRef = useRef(new Map<string, ToastId>());
  const lastPhaseRef = useRef(new Map<string, Phase>());
  const [modalThreadId, setModalThreadId] = useState<ThreadId | null>(null);

  useEffect(() => {
    for (const run of Object.values(runs)) {
      const key = run.threadId as unknown as string;
      const lastPhase = lastPhaseRef.current.get(key);
      if (lastPhase === run.phase) continue;
      lastPhaseRef.current.set(key, run.phase);

      const toastInfo = buildToastDescriptor(run, () => setModalThreadId(run.threadId));
      const existing = toastIdsRef.current.get(key);

      if (existing) {
        toastManager.update(existing, toastInfo);
        if (run.phase === "completed" || run.phase === "error") {
          // Keep the toast visible briefly, then auto-dismiss via toast data.
        }
      } else {
        const toastId = toastManager.add({
          type: toastInfo.type,
          title: toastInfo.title,
          description: toastInfo.description,
          actionProps: toastInfo.actionProps,
          data: { allowCrossThreadVisibility: true },
        });
        toastIdsRef.current.set(key, toastId);
      }
    }
  }, [runs]);

  const activeRun = modalThreadId ? (runs[modalThreadId as unknown as string] ?? null) : null;

  return (
    <ArchiveHookOutputDialog
      open={activeRun !== null}
      run={activeRun}
      onClose={() => setModalThreadId(null)}
    />
  );
}

function buildToastDescriptor(
  run: ArchiveHookRun,
  openModal: () => void,
): {
  type: ArchiveToastType;
  title: string;
  description: string;
  actionProps?: { children: string; onClick: () => void };
} {
  const completedSteps = run.steps.length;
  const totalSteps = run.totalSteps;
  const base = {
    title: `Archive hook${run.phase === "error" ? " falló" : ""}`,
  };

  if (run.phase === "started" || run.phase === "step-started") {
    return {
      type: "loading",
      title: base.title,
      description: `Paso ${Math.max(1, completedSteps + 1)}/${totalSteps}`,
    };
  }

  if (run.phase === "step-finished") {
    const lastStep = run.steps[run.steps.length - 1];
    const label = lastStep ? truncateCommand(lastStep.command) : "";
    return {
      type: "loading",
      title: base.title,
      description: `Paso ${completedSteps}/${totalSteps}: ${label}`,
    };
  }

  if (run.phase === "completed") {
    return {
      type: "success",
      title: "Archive hook completado",
      description: `${completedSteps}/${totalSteps} paso(s) OK`,
      actionProps: { children: "Ver output", onClick: openModal },
    };
  }

  // error
  const failedStep = run.steps.find((step) => step.stepIndex === run.failedStepIndex);
  return {
    type: "error",
    title: base.title,
    description: failedStep
      ? `Paso ${failedStep.stepIndex + 1} falló: ${truncateCommand(failedStep.command)}`
      : "Falló la ejecución del hook",
    actionProps: { children: "Ver output", onClick: openModal },
  };
}

function truncateCommand(cmd: string): string {
  return cmd.length > 60 ? `${cmd.slice(0, 57)}…` : cmd;
}
